// Humanoid bone/joint contract — pure data + pure helpers, no box3d-web runtime
// dependency (only its exported *types* are referenced by consumers, not here).
//
// v1 builds a 13-body / 12-joint ragdoll (see plans/humanoid-lab-plan.md §1-2).
// The full 19-name Unity-HumanBodyBones-style space is reserved for later
// graduation (shoulders/hands/chest as distinct bodies) but not built in v1.

/** Reserved 19-name bone space (Unity HumanBodyBones-style). Not all are built in v1. */
export const HUMAN_BODY_BONE_NAMES_RESERVED = [
  'hips', 'spine', 'chest', 'neck', 'head',
  'leftShoulder', 'leftUpperArm', 'leftLowerArm', 'leftHand',
  'rightShoulder', 'rightUpperArm', 'rightLowerArm', 'rightHand',
  'leftUpperLeg', 'leftLowerLeg', 'leftFoot',
  'rightUpperLeg', 'rightLowerLeg', 'rightFoot',
] as const;

export type HumanoidBoneName =
  | 'hips' | 'spine' | 'head'
  | 'leftUpperArm' | 'rightUpperArm'
  | 'leftLowerArm' | 'rightLowerArm'
  | 'leftUpperLeg' | 'rightUpperLeg'
  | 'leftLowerLeg' | 'rightLowerLeg'
  | 'leftFoot' | 'rightFoot';

export type HumanoidJointName =
  | 'spine' | 'neck'
  | 'leftShoulder' | 'rightShoulder'
  | 'leftElbow' | 'rightElbow'
  | 'leftHip' | 'rightHip'
  | 'leftKnee' | 'rightKnee'
  | 'leftAnkle' | 'rightAnkle';

/** v1's 13 bodies, in the FIXED insertion order `createHumanoid` builds them. */
export const HUMANOID_BONE_NAMES: readonly HumanoidBoneName[] = [
  'hips', 'spine', 'head',
  'leftUpperArm', 'rightUpperArm',
  'leftLowerArm', 'rightLowerArm',
  'leftUpperLeg', 'rightUpperLeg',
  'leftLowerLeg', 'rightLowerLeg',
  'leftFoot', 'rightFoot',
] as const;

/** v1's 12 joints, in the FIXED insertion order `createHumanoid` builds them. */
export const HUMANOID_JOINT_NAMES: readonly HumanoidJointName[] = [
  'spine', 'neck',
  'leftShoulder', 'rightShoulder',
  'leftElbow', 'rightElbow',
  'leftHip', 'rightHip',
  'leftKnee', 'rightKnee',
  'leftAnkle', 'rightAnkle',
] as const;

export type HumanoidJointType = 'spherical' | 'revolute';

export interface HumanoidJointDef {
  readonly name: HumanoidJointName;
  readonly parent: HumanoidBoneName;
  readonly child: HumanoidBoneName;
  readonly type: HumanoidJointType;
}

/** Parent → child pairs never collide (box3d-web `collideConnected` defaults
 *  false). Non-adjacent parts (e.g. arm vs torso) colliding is desired. */
export const HUMANOID_JOINT_DEFS: readonly HumanoidJointDef[] = [
  { name: 'spine', parent: 'hips', child: 'spine', type: 'spherical' },
  { name: 'neck', parent: 'spine', child: 'head', type: 'spherical' },
  { name: 'leftShoulder', parent: 'spine', child: 'leftUpperArm', type: 'spherical' },
  { name: 'rightShoulder', parent: 'spine', child: 'rightUpperArm', type: 'spherical' },
  { name: 'leftElbow', parent: 'leftUpperArm', child: 'leftLowerArm', type: 'revolute' },
  { name: 'rightElbow', parent: 'rightUpperArm', child: 'rightLowerArm', type: 'revolute' },
  { name: 'leftHip', parent: 'hips', child: 'leftUpperLeg', type: 'spherical' },
  { name: 'rightHip', parent: 'hips', child: 'rightUpperLeg', type: 'spherical' },
  { name: 'leftKnee', parent: 'leftUpperLeg', child: 'leftLowerLeg', type: 'revolute' },
  { name: 'rightKnee', parent: 'rightUpperLeg', child: 'rightLowerLeg', type: 'revolute' },
  { name: 'leftAnkle', parent: 'leftLowerLeg', child: 'leftFoot', type: 'revolute' },
  { name: 'rightAnkle', parent: 'rightLowerLeg', child: 'rightFoot', type: 'revolute' },
] as const;

/**
 * Joint-layout preset (checkpoint 20).
 * - `default`: bit-compatible with certified balance/etc (revolute ankles X-only).
 * - `muscleBalance`: spherical ankles with pitch+roll authority; muscleBalance-only.
 */
export type JointLayout = 'default' | 'muscleBalance';

/** Resolve joint defs for a layout. Default is the frozen global table. */
export function resolveJointDefs(layout: JointLayout = 'default'): readonly HumanoidJointDef[] {
  if (layout === 'default') return HUMANOID_JOINT_DEFS;
  return HUMANOID_JOINT_DEFS.map((def) =>
    def.name === 'leftAnkle' || def.name === 'rightAnkle'
      ? { ...def, type: 'spherical' as const }
      : def,
  );
}

// --- mass fractions (de Leva 1996, hands folded into forearms) ---
//
// Trunk (thorax+abdomen+pelvis, ~43.46% of de Leva's male total) is split
// between the hips and spine bodies. The plan's illustrative 26/17.5 split
// gives a spine:upperArm adjacent-mass ratio of ~6.46:1, which fails the
// resolveHumanoidSpec adjacent-mass-ratio guard (<=5:1) at the shoulder
// joint. Re-split to 29/13.4 (trunk total within ~2.5% of the de Leva figure,
// ratios all <=5:1) rather than weaken or delete the guard.
const RAW_MASS_FRACTIONS_PERCENT: Readonly<Record<HumanoidBoneName, number>> = {
  hips: 29.0,
  spine: 13.4,
  head: 6.94,
  leftUpperArm: 2.71,
  rightUpperArm: 2.71,
  leftLowerArm: 2.23,
  rightLowerArm: 2.23,
  leftUpperLeg: 14.16,
  rightUpperLeg: 14.16,
  leftLowerLeg: 4.33,
  rightLowerLeg: 4.33,
  leftFoot: 1.37,
  rightFoot: 1.37,
};

const RAW_MASS_TOTAL = HUMANOID_BONE_NAMES.reduce(
  (sum, bone) => sum + RAW_MASS_FRACTIONS_PERCENT[bone],
  0,
);

/** Renormalized so `Object.values(...)` sums to exactly 1.0. */
export const HUMANOID_MASS_FRACTIONS: Readonly<Record<HumanoidBoneName, number>> =
  Object.fromEntries(
    HUMANOID_BONE_NAMES.map((bone) => [bone, RAW_MASS_FRACTIONS_PERCENT[bone] / RAW_MASS_TOTAL]),
  ) as Record<HumanoidBoneName, number>;

export function boneMassKg(bone: HumanoidBoneName, totalMassKg: number): number {
  return HUMANOID_MASS_FRACTIONS[bone] * totalMassKg;
}

// --- shape dimensions ---

export const HUMANOID_REFERENCE_HEIGHT_M = 1.8;

export interface CapsuleDims {
  readonly kind: 'capsule';
  readonly radiusM: number;
  /** Cylinder half-length, EXCLUDING the hemispherical caps (box3d-web convention). */
  readonly halfHeightM: number;
}
export interface BoxDims {
  readonly kind: 'box';
  readonly halfExtentsM: readonly [number, number, number];
}
export interface SphereDims {
  readonly kind: 'sphere';
  readonly radiusM: number;
}
export type BoneShapeDims = CapsuleDims | BoxDims | SphereDims;

/**
 * Shape dims at the reference height/proportion (1.8m, 'average'). Stacking
 * these bottom-up (feet -> lowerLeg -> upperLeg -> hips -> spine -> head)
 * sums to ~1.79m, matching the reference height within rounding.
 */
export const HUMANOID_SHAPE_DIMS_AT_REFERENCE: Readonly<Record<HumanoidBoneName, BoneShapeDims>> = {
  hips: { kind: 'box', halfExtentsM: [0.14, 0.08, 0.10] },
  spine: { kind: 'capsule', radiusM: 0.10, halfHeightM: 0.14 },
  head: { kind: 'sphere', radiusM: 0.10 },
  leftUpperArm: { kind: 'capsule', radiusM: 0.045, halfHeightM: 0.14 },
  rightUpperArm: { kind: 'capsule', radiusM: 0.045, halfHeightM: 0.14 },
  leftLowerArm: { kind: 'capsule', radiusM: 0.04, halfHeightM: 0.13 },
  rightLowerArm: { kind: 'capsule', radiusM: 0.04, halfHeightM: 0.13 },
  leftUpperLeg: { kind: 'capsule', radiusM: 0.065, halfHeightM: 0.16 },
  rightUpperLeg: { kind: 'capsule', radiusM: 0.065, halfHeightM: 0.16 },
  leftLowerLeg: { kind: 'capsule', radiusM: 0.05, halfHeightM: 0.17 },
  rightLowerLeg: { kind: 'capsule', radiusM: 0.05, halfHeightM: 0.17 },
  leftFoot: { kind: 'box', halfExtentsM: [0.13, 0.03, 0.05] },
  rightFoot: { kind: 'box', halfExtentsM: [0.13, 0.03, 0.05] },
};

/**
 * Foot geometry preset.
 * - `default`: legacy game-foot (long axis lateral) used by certified balance/etc.
 * - `anatomical`: ~10 cm wide × 26 cm sagittal adult-ish box (same volume as default);
 *   used only by muscleBalance so other scenarios keep their certified rig.
 */
export type FootLayout = 'default' | 'anatomical';

/**
 * Anatomical foot half-extents at reference height (full 10×6×26 cm).
 * Volume = 8×0.05×0.03×0.13 = 8×0.13×0.03×0.05 = default volume (mass unchanged).
 */
export const HUMANOID_ANATOMICAL_FOOT_HALF_EXTENTS_M: readonly [number, number, number] = [
  0.05, 0.03, 0.13,
];

/**
 * Foot body CoM offset along +Z (forward) so the ankle sits ~25% of foot length
 * from the heel (heel-biased ankle). At reference height: halfZ × 0.5 = 0.065 m.
 */
export const HUMANOID_ANATOMICAL_FOOT_HEEL_BIAS_FRACTION = 0.5;

export type ProportionPreset = 'average' | 'heroic';

/** Per-bone length/size multiplier relative to `HUMANOID_SHAPE_DIMS_AT_REFERENCE`.
 *  'heroic' lengthens torso/limbs and shrinks the head slightly (classic
 *  bigger-than-average game-hero canon vs strict 7.5-head anatomical average). */
export const HUMANOID_PROPORTION_MULTIPLIERS: Readonly<Record<ProportionPreset, Readonly<Record<HumanoidBoneName, number>>>> = {
  average: {
    hips: 1, spine: 1, head: 1,
    leftUpperArm: 1, rightUpperArm: 1,
    leftLowerArm: 1, rightLowerArm: 1,
    leftUpperLeg: 1, rightUpperLeg: 1,
    leftLowerLeg: 1, rightLowerLeg: 1,
    leftFoot: 1, rightFoot: 1,
  },
  heroic: {
    hips: 1.05, spine: 1.1, head: 0.92,
    leftUpperArm: 1.12, rightUpperArm: 1.12,
    leftLowerArm: 1.1, rightLowerArm: 1.1,
    leftUpperLeg: 1.08, rightUpperLeg: 1.08,
    leftLowerLeg: 1.06, rightLowerLeg: 1.06,
    leftFoot: 1.03, rightFoot: 1.03,
  },
};

function boneScale(bone: HumanoidBoneName, heightM: number, proportionPreset: ProportionPreset): number {
  return (heightM / HUMANOID_REFERENCE_HEIGHT_M) * HUMANOID_PROPORTION_MULTIPLIERS[proportionPreset][bone];
}

/** Sagittal heel-bias (m) for anatomical feet; 0 for default layout. */
export function anatomicalFootHeelBiasM(
  heightM: number,
  proportionPreset: ProportionPreset = 'average',
): number {
  const scale = boneScale('leftFoot', heightM, proportionPreset);
  return HUMANOID_ANATOMICAL_FOOT_HALF_EXTENTS_M[2] * scale * HUMANOID_ANATOMICAL_FOOT_HEEL_BIAS_FRACTION;
}

export function scaledBoneShapeDims(
  bone: HumanoidBoneName,
  heightM: number,
  proportionPreset: ProportionPreset = 'average',
  footLayout: FootLayout = 'default',
): BoneShapeDims {
  const scale = boneScale(bone, heightM, proportionPreset);
  if ((bone === 'leftFoot' || bone === 'rightFoot') && footLayout === 'anatomical') {
    const [hx, hy, hz] = HUMANOID_ANATOMICAL_FOOT_HALF_EXTENTS_M;
    return { kind: 'box', halfExtentsM: [hx * scale, hy * scale, hz * scale] };
  }
  const dims = HUMANOID_SHAPE_DIMS_AT_REFERENCE[bone];
  if (dims.kind === 'capsule') {
    return { kind: 'capsule', radiusM: dims.radiusM * scale, halfHeightM: dims.halfHeightM * scale };
  }
  if (dims.kind === 'sphere') {
    return { kind: 'sphere', radiusM: dims.radiusM * scale };
  }
  const [hx, hy, hz] = dims.halfExtentsM;
  return { kind: 'box', halfExtentsM: [hx * scale, hy * scale, hz * scale] };
}

export function boneShapeVolumeM3(dims: BoneShapeDims): number {
  if (dims.kind === 'box') {
    const [hx, hy, hz] = dims.halfExtentsM;
    return 8 * hx * hy * hz;
  }
  if (dims.kind === 'sphere') {
    return (4 / 3) * Math.PI * dims.radiusM ** 3;
  }
  // Capsule = cylinder (excluding caps) + one full sphere (the two hemispherical caps).
  const cylinderVolume = Math.PI * dims.radiusM ** 2 * (2 * dims.halfHeightM);
  const capsVolume = (4 / 3) * Math.PI * dims.radiusM ** 3;
  return cylinderVolume + capsVolume;
}

// --- joint limits ---

export type JointLimitPreset = 'game' | 'anatomical';

export interface SphericalLimitDeg {
  readonly coneDeg: number;
  readonly twistDeg: readonly [number, number];
}
export interface RevoluteLimitDeg {
  readonly loDeg: number;
  readonly hiDeg: number;
}
export type JointLimitDeg = SphericalLimitDeg | RevoluteLimitDeg;

export function isSphericalLimit(limit: JointLimitDeg): limit is SphericalLimitDeg {
  return 'coneDeg' in limit;
}

/**
 * MuscleBalance spherical ankle soft limits (deg). Checkpoint 20.
 * Cone covers combined pitch+roll swing; twist near-zero (yaw).
 * Anatomical-ish: ~±25–30° pitch/roll envelope, ~±3° yaw.
 */
export const MUSCLE_SPHERICAL_ANKLE_LIMITS_DEG: Readonly<SphericalLimitDeg> = {
  coneDeg: 28,
  twistDeg: [-3, 3],
};

/**
 * 'game': tight, Unity-wizard-style defaults for a responsive ragdoll.
 * 'anatomical': looser biomechanical range-of-motion.
 *
 * Elbow/knee straighten to exactly 0deg in the T-pose spawn. A hard limit
 * bound sitting exactly ON that resting angle (loDeg:0 for elbow, hiDeg:0 for
 * knee) put the joint constraint live from frame one — the tiniest solver
 * noise triggered repeated limit "bang", visibly injecting energy (measured:
 * lower-arm angular speed spiking to 60-70 rad/s in the first ~1s of the
 * settle-gate drop, never decaying below the 0.1 rad/s settle threshold
 * within the 5s timeout). Both bounds are pulled 10deg past the straight
 * pose (comfortably clear of the "0-5deg danger zone" this table already
 * documented but didn't follow) — mild hyperextension allowance, not a
 * kinematic change to the T-pose itself.
 */
export const HUMANOID_JOINT_LIMITS_DEG: Readonly<Record<JointLimitPreset, Readonly<Record<HumanoidJointName, JointLimitDeg>>>> = {
  game: {
    spine: { coneDeg: 20, twistDeg: [-15, 15] },
    neck: { coneDeg: 40, twistDeg: [-45, 45] },
    leftShoulder: { coneDeg: 80, twistDeg: [-60, 60] },
    rightShoulder: { coneDeg: 80, twistDeg: [-60, 60] },
    leftElbow: { loDeg: -10, hiDeg: 145 },
    rightElbow: { loDeg: -10, hiDeg: 145 },
    leftHip: { coneDeg: 35, twistDeg: [-20, 20] },
    rightHip: { coneDeg: 35, twistDeg: [-20, 20] },
    leftKnee: { loDeg: -140, hiDeg: 10 },
    rightKnee: { loDeg: -140, hiDeg: 10 },
    leftAnkle: { loDeg: -25, hiDeg: 25 },
    rightAnkle: { loDeg: -25, hiDeg: 25 },
  },
  anatomical: {
    spine: { coneDeg: 35, twistDeg: [-30, 30] },
    neck: { coneDeg: 55, twistDeg: [-70, 70] },
    leftShoulder: { coneDeg: 110, twistDeg: [-90, 90] },
    rightShoulder: { coneDeg: 110, twistDeg: [-90, 90] },
    leftElbow: { loDeg: -10, hiDeg: 150 },
    rightElbow: { loDeg: -10, hiDeg: 150 },
    leftHip: { coneDeg: 60, twistDeg: [-40, 40] },
    rightHip: { coneDeg: 60, twistDeg: [-40, 40] },
    leftKnee: { loDeg: -145, hiDeg: 10 },
    rightKnee: { loDeg: -145, hiDeg: 10 },
    leftAnkle: { loDeg: -35, hiDeg: 35 },
    rightAnkle: { loDeg: -35, hiDeg: 35 },
  },
};

/**
 * Limit table entry for a joint at a given type (layout-aware).
 * Default table keeps revolute ankles; muscleBalance spherical ankles use
 * {@link MUSCLE_SPHERICAL_ANKLE_LIMITS_DEG}.
 */
export function jointLimitDegForType(
  preset: JointLimitPreset,
  joint: HumanoidJointName,
  type: HumanoidJointType,
): JointLimitDeg {
  if (type === 'spherical' && (joint === 'leftAnkle' || joint === 'rightAnkle')) {
    return MUSCLE_SPHERICAL_ANKLE_LIMITS_DEG;
  }
  return HUMANOID_JOINT_LIMITS_DEG[preset][joint];
}

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export interface SphericalLimitRad {
  readonly coneRad: number;
  readonly twistRad: readonly [number, number];
}
export interface RevoluteLimitRad {
  readonly loRad: number;
  readonly hiRad: number;
}
export type JointLimitRad = SphericalLimitRad | RevoluteLimitRad;

export function isSphericalLimitRad(limit: JointLimitRad): limit is SphericalLimitRad {
  return 'coneRad' in limit;
}

/** Layout-aware limit conversion (use actual joint type from the created rig). */
export function jointLimitRadiansForType(
  preset: JointLimitPreset,
  joint: HumanoidJointName,
  type: HumanoidJointType,
): JointLimitRad {
  const deg = jointLimitDegForType(preset, joint, type);
  if (isSphericalLimit(deg)) {
    return {
      coneRad: degToRad(deg.coneDeg),
      twistRad: [degToRad(deg.twistDeg[0]), degToRad(deg.twistDeg[1])],
    };
  }
  return { loRad: degToRad(deg.loDeg), hiRad: degToRad(deg.hiDeg) };
}

export function jointLimitRadians(preset: JointLimitPreset, joint: HumanoidJointName): JointLimitRad {
  // Default global defs (revolute ankles). Prefer jointLimitRadiansForType with
  // the created rig's joint type when layout may differ.
  return jointLimitRadiansForType(preset, joint, jointDef(joint).type);
}

export function jointDef(name: HumanoidJointName): HumanoidJointDef {
  const def = HUMANOID_JOINT_DEFS.find((candidate) => candidate.name === name);
  if (!def) throw new RangeError(`Unknown humanoid joint: ${name}.`);
  return def;
}
